import React from 'react';
import { fs } from '../utils/typography';

const OrganizationFilter = ({
  organizations,
  selectedOrg,
  onOrgChange,
  theme = {},
  tokens,
  showStarredOnly = false,
  onStarredChange,
  hidePrivateEvents = false,
  onHidePrivateChange,
}) => {
  const tc = tokens ? tokens.colors : null;
  const fonts = tokens ? tokens.fonts : null;

  const selectStyle = tc ? {
    background: tc.surfaceAlt,
    color: tc.ink,
    border: `1px solid ${tc.gridLine}`,
    borderRadius: 8,
    padding: '6px 10px',
    fontSize: fs(14),
    fontFamily: fonts ? fonts.ui : undefined,
    outline: 'none',
    width: '100%',
    cursor: 'pointer',
  } : {};

  const clearBtnStyle = tc ? {
    background: tc.surfaceAlt,
    color: tc.muted,
    border: `1px solid ${tc.gridLine}`,
    borderRadius: 8,
    width: 32,
    height: 32,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  } : {};

  const pillBase = tc ? {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 12px',
    borderRadius: 999,
    fontSize: fs(13),
    fontWeight: 500,
    fontFamily: fonts ? fonts.ui : undefined,
    transition: 'background 0.15s, color 0.15s',
    cursor: 'pointer',
    border: `1px solid ${tc.gridLine}`,
    background: tc.surfaceAlt,
    color: tc.muted,
  } : {};

  const pillActive = tc ? {
    ...pillBase,
    background: tc.accent,
    color: tokens.mode === 'dark' ? '#1a1410' : '#fff',
    border: '1px solid transparent',
  } : {};

  const labelStyle = tc ? {
    color: tc.muted,
    fontSize: fs(13),
    fontWeight: 500,
    flexShrink: 0,
    fontFamily: fonts ? fonts.ui : undefined,
  } : {};

  // Fallback to old Tailwind classes when tokens not provided
  if (!tc) {
    const defaultTheme = {
      text: 'text-gray-900',
      secondaryText: 'text-gray-600',
      background: 'bg-white',
      border: 'border-gray-300',
    };
    const currentTheme = { ...defaultTheme, ...theme };
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <label htmlFor="organization-filter" className={`text-sm font-medium ${currentTheme.secondaryText} shrink-0 hidden sm:block`}>
            Organization:
          </label>
          <select
            id="organization-filter"
            value={selectedOrg}
            onChange={(e) => onOrgChange(e.target.value)}
            className={`flex-1 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border ${currentTheme.background} ${currentTheme.text} ${currentTheme.border}`}
            disabled={organizations.length === 0}
            aria-label="Filter events by organization"
          >
            <option value="all">All Organizations</option>
            {organizations.map((org) => (<option key={org} value={org}>{org}</option>))}
          </select>
          {selectedOrg !== 'all' && (
            <button onClick={() => onOrgChange('all')} className="shrink-0 p-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700" aria-label="Clear organization filter">
              <i className="fas fa-times" aria-hidden="true"></i>
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => onStarredChange && onStarredChange(!showStarredOnly)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition border ${showStarredOnly ? currentTheme.favoritesActive || 'bg-red-100 text-red-700 border-red-300' : currentTheme.filterInactive || 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'}`} aria-pressed={showStarredOnly} aria-label={showStarredOnly ? 'Showing favorites only' : 'Show favorites only'}>
            <i className={`${showStarredOnly ? 'fas' : 'far'} fa-heart`} aria-hidden="true"></i>
            Favorites only
          </button>
          <button onClick={() => onHidePrivateChange && onHidePrivateChange(!hidePrivateEvents)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition border ${hidePrivateEvents ? currentTheme.hidePrivateActive || 'bg-blue-100 text-blue-700 border-blue-300' : currentTheme.filterInactive || 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'}`} aria-pressed={hidePrivateEvents} aria-label={hidePrivateEvents ? 'Private events hidden' : 'Hide private events'}>
            <i className="fas fa-eye-slash" aria-hidden="true"></i>
            Hide private
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Org filter row */}
      <div className="flex items-center gap-2">
        <label
          htmlFor="organization-filter"
          className="shrink-0 hidden sm:block"
          style={labelStyle}
        >
          Organization:
        </label>
        <select
          id="organization-filter"
          value={selectedOrg}
          onChange={(e) => onOrgChange(e.target.value)}
          style={selectStyle}
          disabled={organizations.length === 0}
          aria-label="Filter events by organization"
        >
          <option value="all">All Organizations</option>
          {organizations.map((org) => (
            <option key={org} value={org}>{org}</option>
          ))}
        </select>
        {selectedOrg !== 'all' && (
          <button
            onClick={() => onOrgChange('all')}
            style={clearBtnStyle}
            aria-label="Clear organization filter"
          >
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
        )}
      </div>

      {/* Toggle pill buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => onStarredChange && onStarredChange(!showStarredOnly)}
          style={showStarredOnly ? pillActive : pillBase}
          aria-pressed={showStarredOnly}
          aria-label={showStarredOnly ? 'Showing favorites only' : 'Show favorites only'}
        >
          <i className={`${showStarredOnly ? 'fas' : 'far'} fa-heart`} aria-hidden="true"></i>
          Favorites only
        </button>

        <button
          onClick={() => onHidePrivateChange && onHidePrivateChange(!hidePrivateEvents)}
          style={hidePrivateEvents ? pillActive : pillBase}
          aria-pressed={hidePrivateEvents}
          aria-label={hidePrivateEvents ? 'Private events hidden' : 'Hide private events'}
        >
          <i className="fas fa-eye-slash" aria-hidden="true"></i>
          Hide private
        </button>
      </div>
    </div>
  );
};

export default OrganizationFilter;
